import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import {
    catchError,
    exhaustMap,
    filter,
    map,
    of,
    switchMap,
    withLatestFrom,
} from "rxjs";
import { CredentialStore } from "../../authentication/data-access/credential.store";
import { SessionActions } from "../../authentication/store/session.actions";
import { sessionFeature } from "../../authentication/store/session.reducer";
import { CompanyBillingApiService } from "../data-access/company-billing-api.service";
import { resolveCompanyEntitlement } from "../services/company-entitlement.resolver";
import { CompanyEntitlementActions } from "./company-entitlement.actions";
import { companyEntitlementFeature } from "./company-entitlement.reducer";

@Injectable()
export class CompanyEntitlementEffects {
    private readonly actions$ = inject(Actions);
    private readonly api = inject(CompanyBillingApiService);
    private readonly credentials = inject(CredentialStore);
    private readonly store = inject(Store);
    private readonly translate = inject(TranslateService);

    readonly refresh$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CompanyEntitlementActions.refreshRequested),
            withLatestFrom(
                this.store.select(companyEntitlementFeature.selectClientId),
                this.store.select(sessionFeature.selectUser)
            ),
            filter(
                ([, clientId, user]) =>
                    Boolean(clientId) && user?.role === "user"
            ),
            map(([, clientId]) =>
                CompanyEntitlementActions.loadRequested({
                    clientId: clientId!,
                })
            )
        )
    );

    readonly load$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CompanyEntitlementActions.loadRequested),
            withLatestFrom(this.store.select(sessionFeature.selectUser)),
            filter(([, user]) => user?.role === "user"),
            switchMap(([{ clientId }]) =>
                this.api.load(clientId).pipe(
                    map((snapshot) =>
                        CompanyEntitlementActions.loadSucceeded({
                            entitlement: resolveCompanyEntitlement(
                                snapshot,
                                this.credentials.readTokens()?.source
                            ),
                            company: snapshot.company,
                        })
                    ),
                    catchError((error: unknown) =>
                        of(
                            CompanyEntitlementActions.loadFailed({
                                message:
                                    error instanceof Error
                                        ? error.message
                                        : this.translate.instant(
                                              "billing.errors.unavailable"
                                          ),
                            })
                        )
                    )
                )
            )
        )
    );

    readonly clear$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SessionActions.logoutCompleted),
            map(() => CompanyEntitlementActions.cleared())
        )
    );
}
